import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTimezone } from '@contexts/useTimezone';
import { Organization } from '@models/Organization';
import { DashboardService } from '@services/DashboardService';
import { RosterService, RosterServiceModel } from '@services/RosterService';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { Logger } from '@utils/Logger';
import Utils from '@utils/Utils';

const EMPTY_SUMMARY: RosterServiceModel.RosterSummary = {
    total: 0,
    presente: 0,
    conDemora: 0,
    sinPresentarse: 0,
    ausente: 0,
    planificado: 0,
};

const STATUS_COLORS: Record<RosterServiceModel.RosterStatus, string> = {
    PRESENTE: designSystemStyles.colorSuccess,
    CON_DEMORA: designSystemStyles.colorWarning,
    SIN_PRESENTARSE: designSystemStyles.colorMainAction,
    AUSENTE: designSystemStyles.colorRejected,
    PLANIFICADO: designSystemStyles.colorInProgress,
};

const STATUS_LABELS: Record<RosterServiceModel.RosterStatus, string> = {
    PRESENTE: 'Presente',
    CON_DEMORA: 'Tarde',
    SIN_PRESENTARSE: 'Sin presentarse',
    AUSENTE: 'Ausente',
    PLANIFICADO: 'Planificado',
};

export interface EnrichedRosterRow extends RosterServiceModel.RosterRow {
    checkOutTimestamp: number | null;
}

export const rosterStatusColor = (status: RosterServiceModel.RosterStatus): string =>
    STATUS_COLORS[status];

export const rosterStatusLabel = (status: RosterServiceModel.RosterStatus): string =>
    STATUS_LABELS[status].localized();

export const formatCheckinTime = (
    timestamp: number | null,
    timezone: string,
): string => {
    if (timestamp == null) return '—';
    return new Date(timestamp).toLocaleTimeString('es-AR', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
};

function buildCheckoutLookup(data: any): Map<number, number | null> {
    const lookup = new Map<number, number | null>();
    if (!data?.buildings) return lookup;

    for (const building of Object.values<any>(data.buildings)) {
        if (!building?.user_records) continue;
        for (const userRecord of Object.values<any>(building.user_records)) {
            if (!userRecord?.records) continue;
            for (const record of Object.values<any>(userRecord.records)) {
                const checkInTs = Number(record?.checkInTimestamp ?? record?.timestamp);
                if (!Number.isFinite(checkInTs) || checkInTs === 0) continue;
                const checkOutTs = Number(record?.checkOutTimestamp ?? 0);
                lookup.set(checkInTs, checkOutTs > 0 ? checkOutTs : null);
            }
        }
    }

    return lookup;
}

interface UseRosterScreenViewModelProps {
    org: Organization;
    rosterService: RosterService;
    dashboardService: DashboardService;
}

export const useRosterScreenViewModel = ({ org, rosterService, dashboardService }: UseRosterScreenViewModelProps) => {
    const { timezone } = useTimezone();
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [rows, setRows] = useState<EnrichedRosterRow[]>([]);
    const [summary, setSummary] = useState<RosterServiceModel.RosterSummary>(EMPTY_SUMMARY);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<RosterServiceModel.RosterStatus | null>(null);

    const day = useMemo(
        () => Utils.getDayStringFromTimestamp(selectedDate.getTime(), timezone),
        [selectedDate, timezone],
    );

    const formattedDay = useMemo(
        () =>
            selectedDate.toLocaleDateString('es-AR', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
        [selectedDate, timezone],
    );

    const enrichedWithAusente = useMemo(() => {
        const now = Date.now();
        return rows.map(r => {
            if (r.status !== 'SIN_PRESENTARSE') return r;
            const [h, m] = r.hourTo.split(':').map(Number);
            if (isNaN(h) || isNaN(m)) return r;
            // Calendar date of the shift in the org's timezone
            const dateStr = new Date(r.timestampFrom).toLocaleDateString('en-CA', { timeZone: timezone });
            // Parse the shift-end wall-clock time as if it were UTC, then correct for the timezone offset
            const naiveUtc = Date.UTC(
                Number(dateStr.slice(0, 4)),
                Number(dateStr.slice(5, 7)) - 1,
                Number(dateStr.slice(8, 10)),
                h, m, 0,
            );
            // Get the UTC offset for this timezone at this moment (in ms)
            const localStr = new Date(naiveUtc).toLocaleString('en-US', { timeZone: timezone, hour12: false, hour: '2-digit', minute: '2-digit', timeZoneName: 'shortOffset' });
            const offsetMatch = localStr.match(/GMT([+-]\d+(?::\d+)?)/);
            const offsetHours = offsetMatch ? parseFloat(offsetMatch[1]) : 0;
            const shiftEndUtc = naiveUtc - offsetHours * 3600000;
            return shiftEndUtc < now ? { ...r, status: 'AUSENTE' as const } : r;
        });
    }, [rows, timezone]);

    const derivedSummary = useMemo((): RosterServiceModel.RosterSummary => ({
        total: enrichedWithAusente.length,
        presente: enrichedWithAusente.filter(r => r.status === 'PRESENTE').length,
        conDemora: enrichedWithAusente.filter(r => r.status === 'CON_DEMORA').length,
        sinPresentarse: enrichedWithAusente.filter(r => r.status === 'SIN_PRESENTARSE').length,
        ausente: enrichedWithAusente.filter(r => r.status === 'AUSENTE').length,
        planificado: enrichedWithAusente.filter(r => r.status === 'PLANIFICADO').length,
    }), [enrichedWithAusente]);

    const filteredRows = useMemo(() => {
        let result = enrichedWithAusente;
        if (statusFilter != null) result = result.filter(r => r.status === statusFilter);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(r => r.userName.toLowerCase().includes(q));
        }
        return result;
    }, [enrichedWithAusente, searchQuery, statusFilter]);

    const fetchRoster = useCallback(async () => {
        if (!org.id) return;
        setLoading(true);
        try {
            const [rosterResponse, presenteeismData] = await Promise.all([
                rosterService.getRosterForDay(org.id, day),
                dashboardService.getPresenteeismTableDay(org.id, day).catch(() => null),
            ]);

            const checkoutLookup = buildCheckoutLookup(presenteeismData);

            const enrichedRows: EnrichedRosterRow[] = rosterResponse.rows.map(row => ({
                ...row,
                checkOutTimestamp: row.actualCheckinTimestamp != null
                    ? (checkoutLookup.get(row.actualCheckinTimestamp) ?? null)
                    : null,
            }));

            setRows(enrichedRows);
            setSummary(rosterResponse.summary);
        } catch (error) {
            Logger.error(`[RosterScreenViewModel] fetchRoster failed: ${String(error)}`);
            setRows([]);
            setSummary(EMPTY_SUMMARY);
        } finally {
            setLoading(false);
        }
    }, [org.id, day, rosterService, dashboardService]);

    useEffect(() => {
        void fetchRoster();
    }, [fetchRoster]);

    return {
        filteredRows,
        summary: derivedSummary,
        loading,
        day,
        selectedDate,
        setSelectedDate,
        formattedDay,
        timezone,
        searchQuery,
        setSearchQuery,
        statusFilter,
        setStatusFilter,
        refresh: fetchRoster,
    };
};
